import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  useAliases,
  useCreateAlias,
  useDeleteAlias,
} from "@/api/hooks";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const schema = z.object({
  address: z.string().email(),
  goto: z.string().email(),
});

export default function AliasesPage() {
  const { data, isLoading } = useAliases();
  const del = useDeleteAlias();

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="text-heading text-3xl font-light sm:text-4xl">
            <span className="text-bitrefinery-red">A</span>liases
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-bitrefinery-text text-body">
            Forward one address to another. Multiple entries with the same alias
            fan out to all targets.
          </p>
        </div>
        <CreateAliasDialog />
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Alias</TableHead>
              <TableHead>Forwards to</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[1%] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {data?.aliases.map((a) => (
              <TableRow key={`${a.address}->${a.goto}`}>
                <TableCell className="font-mono text-sm">{a.address}</TableCell>
                <TableCell className="font-mono text-sm text-muted-foreground">
                  {a.goto}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {new Date(a.created_at).toLocaleString()}
                </TableCell>
                <TableCell className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => del.mutate({ address: a.address, goto: a.goto })}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {data && data.aliases.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  No aliases yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function CreateAliasDialog() {
  const [open, setOpen] = useState(false);
  const create = useCreateAlias();
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema) });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="h-4 w-4" />
          New alias
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create alias</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit(async (v) => {
            try {
              await create.mutateAsync(v);
              form.reset();
              setOpen(false);
            } catch (err) {
              form.setError("address", {
                message: err instanceof ApiError ? err.code : "failed",
              });
            }
          })}
        >
          <div className="space-y-1.5">
            <Label htmlFor="address">Alias address</Label>
            <Input id="address" placeholder="postmaster@aigrammys.com" {...form.register("address")} />
            {form.formState.errors.address && (
              <p className="text-xs text-destructive">
                {form.formState.errors.address.message}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="goto">Forward to</Label>
            <Input id="goto" placeholder="tn@aigrammys.com" {...form.register("goto")} />
            {form.formState.errors.goto && (
              <p className="text-xs text-destructive">
                {form.formState.errors.goto.message}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
